// Third Party
import React from 'react';

// Namespace
import * as ProposalNS from '../../../Proposal';

// Utils
import { getProposalCoBrandingColor, getTabElements } from '@/modules/proposal/ProposalHelpers';
import { formatMessage, LOCALE } from '@/utils/i18n/i18n';
import { PROPOSAL_BANNER_STYLES, PROPOSAL_ORIENTATION, PROPOSAL_PAPER_FORMAT, PROPOSAL_PATTERN } from '@/modules/proposal/utils/constants';
import { useProposalActions } from '../../preview/ProposalActionsContext';

// Types
import User from '@/types/user';

// Components
import { RadioGroup } from '@/views/components/inputs';
import CollapsibleSection from './CollapsibleSection';
import { ColorSelector, PatternSelector, ColorSwatcher } from '@/views/components/misc';
import { ProposalSidebarOptions } from '../../proposal-sidebar/ProposalSidebar';

type OptionsSidebarProps = {
  sidebar: ProposalNS.ProposalSidebarTab;
  user: User;
  proposal: ProposalNS.Proposal;
  proposalTemplateOptions: ProposalNS.ProposalTemplateOptions;
};

const OptionsSidebar = ({ sidebar, user, proposal, proposalTemplateOptions }: OptionsSidebarProps) => {
  const {
    appConfig: { appLocales },
  } = user;
  const { onOrientationChange, onHeaderColorChange, onHeaderPatternChange, onCoBrandingColorChange, onPaperFormatChange, onLocaleChange } =
    useProposalActions();
  const availableLocales = Object.values(LOCALE).filter(locale => appLocales.includes(locale));
  const elements = getTabElements(sidebar, user);

  const renderElement = (element: ProposalNS.ProposalSidebarElement, index: number) => {
    switch (element.type) {
      case ProposalSidebarOptions.LANGUAGE:
        const languageRadioItems = availableLocales.map(locale => ({
          value: locale,
          label: formatMessage(`Proposal.Tabs.Options.Language.${locale}`),
        }));
        const selectedLanguage = languageRadioItems.find(item => item.value === proposal.options.locale);
        const ariaLabelIdLanguage = 'edit-modal-proposal-language';
        return (
          <CollapsibleSection key={index} title={formatMessage('Proposal.Tabs.Options.Language.Title')} summaryId={ariaLabelIdLanguage}>
            <RadioGroup
              items={languageRadioItems}
              value={selectedLanguage}
              onChange={item => onLocaleChange(item.value)}
              ariaLabelledby={ariaLabelIdLanguage}
            />
          </CollapsibleSection>
        );

      case ProposalSidebarOptions.PATTERN_SELECTOR:
        return (
          <CollapsibleSection key={index} title={formatMessage('Proposal.Tabs.Options.HeaderPattern')}>
            <PatternSelector
              patterns={Object.keys(PROPOSAL_PATTERN)}
              selected={proposalTemplateOptions.styleOptions.banners.pattern}
              onChange={onHeaderPatternChange}
            />
          </CollapsibleSection>
        );

      case ProposalSidebarOptions.COLOR_SELECTOR:
        return (
          <CollapsibleSection key={index} title={formatMessage('Proposal.Tabs.Options.HeaderColor')}>
            <ColorSelector
              colors={PROPOSAL_BANNER_STYLES.map(s => s.background)}
              selected={proposalTemplateOptions.styleOptions.banners.background}
              onChange={onHeaderColorChange}
            />
          </CollapsibleSection>
        );

      case ProposalSidebarOptions.ORIENTATION:
        const orientationRadioItems = Object.values(PROPOSAL_ORIENTATION).map(orientation => ({
          value: orientation,
          label: formatMessage(`Proposal.Tabs.Options.Orientations.${orientation}`),
        }));
        const selectedOrientation = orientationRadioItems.find(item => item.value === proposalTemplateOptions.styleOptions.orientation);
        const ariaLabelIdOrientation = 'edit-modal-proposal-orientation';
        return (
          <CollapsibleSection key={index} title={formatMessage('Proposal.Tabs.Options.Orientation')} summaryId={ariaLabelIdOrientation}>
            <RadioGroup
              items={orientationRadioItems}
              value={selectedOrientation}
              onChange={item => onOrientationChange(item.value)}
              ariaLabelledby={ariaLabelIdOrientation}
            />
          </CollapsibleSection>
        );

      case ProposalSidebarOptions.PAPER_FORMAT:
        const paperRadioItems = Object.values(PROPOSAL_PAPER_FORMAT).map(format => ({
          value: format,
          label: formatMessage(`Proposal.Tabs.Options.PaperFormat.${format}`),
        }));
        const selectedPaperFormat = paperRadioItems.find(item => item.value === proposalTemplateOptions.styleOptions.paperFormat);
        const ariaLabelIdPaperFormat = 'edit-modal-proposal-paper-format';
        return (
          <CollapsibleSection key={index} title={formatMessage('Proposal.Tabs.Options.PaperFormat.Title')} summaryId={ariaLabelIdPaperFormat}>
            <RadioGroup
              items={paperRadioItems}
              value={selectedPaperFormat}
              onChange={item => onPaperFormatChange(item.value)}
              ariaLabelledby={ariaLabelIdPaperFormat}
            />
          </CollapsibleSection>
        );

      case ProposalSidebarOptions.COBRANDING_COLOR:
        const selectedCoBrandingColor = getProposalCoBrandingColor(user, proposalTemplateOptions.styleOptions);
        return (
          <CollapsibleSection key={index} title={formatMessage('Proposal.Tabs.Options.BrandingColor')}>
            <ColorSwatcher selected={selectedCoBrandingColor} onChange={color => onCoBrandingColorChange(color, 'primary')} />
          </CollapsibleSection>
        );

      default:
        return null;
    }
  };

  return <div>{elements.map((element, index) => renderElement(element, index))}</div>;
};

export default OptionsSidebar;
